import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Users, Clock, Trash2, Edit } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import WorkPermitCreation from "./WPCreateOrder";
import QuickPlanModal from "./WPQuickPlanModal";

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, "")}/api`;



const TeamDayView = ({ entity, date, viewType, dayOrders, onClose, refreshParentData }) => {
  const [orderDetails, setOrderDetails] = useState(
    dayOrders.map((order) => ({
      ...order,
      isEditingTime: false,
    }))
  );
  const [editingTimes, setEditingTimes] = useState({});
  const [isWorkPermitOpen, setIsWorkPermitOpen] = useState(false);
  const [selectedOrderForEdit, setSelectedOrderForEdit] = useState(null);
  const [showQuickPlanModal, setShowQuickPlanModal] = useState(false);

  const teamMembers = viewType === "teams"
    ? entity.users || []
    : [{ id: entity.id, first_name: entity.name.split(" ")[1] || "", last_name: entity.name.split(" ")[0] || "" }];

  const supervisor = viewType === "teams" ? entity.users.find((u) => u.id === entity.team_leader) : null;
  const preparer = viewType === "teams"
    ? entity.users.find((u) => u.id === entity.person_responsible_for_preparation_of_work_place)
    : null;
  const otherMembers = viewType === "teams"
    ? entity.users.filter(
        (u) => u.id !== entity.team_leader && u.id !== entity.person_responsible_for_preparation_of_work_place
      )
    : [];

  const startEditing = (order) => {
    if (viewType !== "teams") return; // Edycja możliwa tylko w widoku "Teams"
    setEditingTimes({
      ...editingTimes,
      [order.id]: {
        startTime: new Date(order.planned_date_start).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
        endTime: new Date(order.planned_date_end).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
      },
    });
    setOrderDetails((prev) =>
      prev.map((o) => (o.id === order.id ? { ...o, isEditingTime: true } : o))
    );
  };

  const saveTimeChanges = async (order) => {
    try {
      const token = localStorage.getItem("access_token");
      const currentOrderResponse = await axios.get(
        `${API_BASE_URL}/workpermit/${order.id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const startDate = new Date(order.planned_date_start);
      const [startHours, startMinutes] = editingTimes[order.id].startTime.split(":");
      startDate.setHours(parseInt(startHours), parseInt(startMinutes));

      const endDate = new Date(order.planned_date_end);
      const [endHours, endMinutes] = editingTimes[order.id].endTime.split(":");
      endDate.setHours(parseInt(endHours), parseInt(endMinutes));

      const updateData = {
        ...currentOrderResponse.data,
        planned_date_start: startDate.toISOString(),
        planned_date_end: endDate.toISOString(),
        status: "planned",
        team: viewType === "teams" ? entity.id : currentOrderResponse.data.team,
      };

      await axios.put(`${API_BASE_URL}/workpermit/${order.id}/`, updateData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });

      const newOrderDetails = [...orderDetails];
      const orderIndex = newOrderDetails.findIndex((o) => o.id === order.id);
      if (orderIndex !== -1) {
        newOrderDetails[orderIndex] = {
          ...newOrderDetails[orderIndex],
          planned_date_start: startDate.toISOString(),
          planned_date_end: endDate.toISOString(),
          status: "planned",
          isEditingTime: false,
        };
        setOrderDetails(newOrderDetails);
      }

      setEditingTimes((prev) => {
        const newEditingTimes = { ...prev };
        delete newEditingTimes[order.id];
        return newEditingTimes;
      });
    } catch (error) {
      console.error("Error updating time:", error);
      toast.error("Error updating time");
    }
  };

  const cancelEditing = (orderId) => {
    setOrderDetails((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, isEditingTime: false } : o))
    );
    setEditingTimes((prev) => {
      const newEditingTimes = { ...prev };
      delete newEditingTimes[orderId];
      return newEditingTimes;
    });
  };

  const removeOrder = async (order) => {
    try {
      const token = localStorage.getItem("access_token");
      const updateData = {
        status: "pending",
        planned_date_start: null,
        planned_date_end: null,
        sign_date: null,
        team: null,
      };

      await axios.patch(`${API_BASE_URL}/workpermit/${order.id}/`, updateData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });

      setOrderDetails((prev) => prev.filter((o) => o.id !== order.id));
      toast.success(`Order ${order.work_permit_number} removed successfully`);
      if (refreshParentData) refreshParentData();
    } catch (error) {
      console.error("Error removing order:", error);
      toast.error("Error removing order");
    }
  };

  const getOrderBackgroundClass = (status) => {
    switch (status) {
      case "planned": return "bg-blue-50 border-blue-200";
      case "planned_approved": return "bg-orange-50 border-orange-200";
      case "signed": return "bg-yellow-50 border-yellow-200";
      case "approved": return "bg-green-50 border-green-200";
      case "rejected": return "bg-red-50 border-red-200";
      case "closed": return "bg-gray-50 border-gray-200";
      default: return "bg-white border-gray-300";
    }
  };

  const canRemoveOrder = (status) => ["planned", "signed", "planned_approved"].includes(status);

  const handleAddNewOrder = () => {
    if (viewType === "teams") {
      setShowQuickPlanModal(true);
    } else {
      // For people view, use old modal
      setSelectedOrderForEdit(null);
      setIsWorkPermitOpen(true);
    }
  };

  const handleEditOrder = (order) => {
    setSelectedOrderForEdit(order);
    setIsWorkPermitOpen(true);
  };

  const handleWorkPermitClose = () => {
    setIsWorkPermitOpen(false);
    setSelectedOrderForEdit(null);
  };

  const handleWorkPermitSuccess = () => {
    setIsWorkPermitOpen(false);
    setSelectedOrderForEdit(null);
    onClose();
    if (refreshParentData) refreshParentData();
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
  <DialogTitle className="text-sm">
    {entity.name} -{" "}
    {new Date(date).toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })}
  </DialogTitle>
  <div className="mt-2 space-y-2">
    {viewType === "teams" && (
      <>
        <div>
          <div className="text-[10px] font-medium">Supervisor</div>
          {supervisor ? (
            <div className="text-[10px] text-gray-600 flex items-center">
              <Users className="h-2 w-2 mr-1" />
              {supervisor.first_name} {supervisor.last_name}
            </div>
          ) : (
            <div className="text-[10px] text-gray-500">No supervisor assigned</div>
          )}
        </div>
        <div>
          <div className="text-[10px] font-medium">Preparer</div>
          {preparer ? (
            <div className="text-[10px] text-gray-600 flex items-center">
              <Users className="h-2 w-2 mr-1" />
              {preparer.first_name} {preparer.last_name}
            </div>
          ) : (
            <div className="text-[10px] text-gray-500">No preparer assigned</div>
          )}
        </div>
        {otherMembers.length > 0 && (
          <div>
            <div className="text-[10px] font-medium">Other Team Members</div>
            <div className="space-y-1">
              {otherMembers.map((member, index) => (
                <div key={index} className="text-[10px] text-gray-600 flex items-center">
                  <Users className="h-2 w-2 mr-1" />
                  {member.first_name} {member.last_name}
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    )}
  </div>
</DialogHeader>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-[10px] text-gray-500">Orders for this day</p>
            </div>
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {orderDetails.map((order, index) => {
                const isDuplicate = orderDetails.filter(
                  (o) => o.work_permit_number === order.work_permit_number
                ).length > 1;
                const backgroundClass = getOrderBackgroundClass(order.status);

                return (
                  <div
                    key={index}
                    className={`p-2 border rounded hover:bg-gray-50 ${backgroundClass} ${isDuplicate ? "bg-red-100 border-red-200" : ""}`}
                  >
                    <div className="space-y-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-[12px]">
                            {order.work_permit_number}
                            {isDuplicate && <span className="text-red-600 text-[10px] ml-1">(Duplicate)</span>}
                          </div>
                          <div className="text-[10px] text-gray-600 mt-1">Location: {order.turbine_data.location}</div>
                        </div>
                        <span
                          className={`px-1 py-0.5 rounded-full text-[10px] ${
                            order.status === "planned" ? "bg-blue-100 text-blue-800" :
                            order.status === "planned_approved" ? "bg-orange-100 text-orange-800" :
                            order.status === "signed" ? "bg-yellow-100 text-yellow-800" :
                            order.status === "approved" ? "bg-green-100 text-green-800" :
                            order.status === "rejected" ? "bg-red-100 text-red-800" :
                            order.status === "closed" ? "bg-gray-100 text-gray-800" :
                            "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {order.status || "unknown"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-[10px]">
                        <div>
                          <div className="font-medium">Turbine</div>
                          <div className="text-gray-600">{order.turbine_data.type_data.name} - {order.turbine_data.turbine_number}</div>
                        </div>
                        <div>
                          <div className="font-medium">Work Scope</div>
                          <div className="text-gray-600">{order.work_scope_data.name}</div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <div className="text-[10px] font-medium">Location</div>
                          <div className="text-[10px] text-gray-600">
                            {order.turbine_data?.location || "No location"}
                          </div>
                        </div>
                        <div className="flex justify-end gap-1 mt-1">
                          {viewType === "teams" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditOrder(order)}
                                className="text-[10px]"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Edit
                              </Button>
                              {canRemoveOrder(order.status) && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => removeOrder(order)}
                                  className="text-[10px]"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Remove
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {order.comments && (
                        <div>
                          <div className="text-[10px] font-medium">Comments</div>
                          <div className="text-[10px] text-gray-600">{order.comments}</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {orderDetails.length === 0 && (
                <div className="text-gray-500 text-center py-1 text-[10px]">
                  No orders for this day
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={onClose} className="px-4 py-2 bg-white text-black rounded-full hover:bg-gray-100">Close</Button>
          {viewType === "teams" && (
            <Button variant="outline" onClick={handleAddNewOrder} className="px-4 py-2 bg-white text-black rounded-full hover:bg-gray-100">
              {orderDetails.length === 0 ? 'Add New Order' : 'Add Next Order'}
            </Button>
          )}

        </DialogFooter>
        </DialogContent>
      </Dialog>
      {isWorkPermitOpen && (
        <WorkPermitCreation
          isOpen={isWorkPermitOpen}
          onClose={handleWorkPermitClose}
          refreshPreOrders={handleWorkPermitSuccess}
          team={viewType === "teams" ? entity : null}
          data={selectedOrderForEdit}
        />
      )}

      <QuickPlanModal
        isOpen={showQuickPlanModal}
        onClose={() => setShowQuickPlanModal(false)}
        date={date}
        teamId={viewType === "teams" ? entity.id : null}
        teamName={viewType === "teams" ? entity.name : null}
        onSuccess={handleWorkPermitSuccess}
      />
    </>
  );
};

export default TeamDayView;